import { PageTitle, H2Heading } from "../../../components/Headers";
import { PageBody } from "../../../components/PageBody";
import { Helmet } from "react-helmet-async";
import { ArticleParagraph } from "../../../components/Paragraphs";
import { Link } from "react-router-dom";

export const GolferDataModel = () => {
    return (
        <PageBody>
            <Helmet>
                <title>The Golfer Data Model | VizBadger</title>
                <meta
                    name="description"
                    content="How the golf simulation represents a golfer's ability: handicap as universal proxy, strokes-gained-inspired skill shape, consistency, course fit, and situational factors."
                />
            </Helmet>

            <PageTitle title="The Golfer Data Model" />

            <ArticleParagraph
                lines={[
                    "To simulate golf meaningfully, we need a way to represent a golfer that captures more than just \"how good they are\". A single number — even one as well-established as the handicap index — loses the texture of how a player actually plays. Two golfers with identical handicaps can have very different games, and those differences matter when they interact with a specific course, weather conditions, or tournament pressure.",
                    "The model distils a golfer into a handful of attributes that interact with each other and with the course. Each attribute is deliberately simple — the goal is not to replicate every nuance of the game, but to capture enough structure for differentiated, plausible outcomes.",
                ]}
            />

            <H2Heading>Handicap index</H2Heading>
            <ArticleParagraph
                lines={[
                    "The handicap index is the backbone of the model. It represents a golfer's overall ability on a standardised scale, adjusted for course difficulty via the slope and course rating system. This is the primary axis: it determines expected scoring, and all other attributes modify the shape of performance around that baseline. We use it because it is universal, well-understood, and already accounts for the difference between playing a 6,200-yard parkland course and a 7,400-yard links.",
                ]}
            />

            <H2Heading>Skill shape</H2Heading>
            <ArticleParagraph
                lines={[
                    "Handicap tells you the level; skill shape tells you the profile. Four categories — inspired by strokes gained analysis — describe where a golfer's ability is concentrated: off the tee, approach play, around the green, and putting. Each is expressed as a modifier ranging from negative (weaker than the handicap suggests) to positive (stronger). The modifiers do not change the golfer's overall expected score — they describe how that score is constructed.",
                    "Two 10-handicappers might score similarly overall, but one reaches greens in regulation and struggles to putt, while the other scrambles brilliantly from poor positions. On a course with large, undulating greens, the first golfer has an advantage. On a tight layout that demands recovery play, the second does. This is the purpose of skill shape: to make course fit meaningful.",
                ]}
            />

            <H2Heading>Consistency</H2Heading>
            <ArticleParagraph
                lines={[
                    "Consistency captures how reliably a golfer reproduces their expected performance. A low-consistency golfer has wider score variance — more brilliant rounds, but also more poor ones. A high-consistency golfer is predictable, rarely far from their average. In a single round this matters less, but across a four-round tournament, consistency compounds: a volatile player might lead after day one and miss the cut after day two.",
                ]}
            />

            <H2Heading>Course fit</H2Heading>
            <ArticleParagraph
                lines={[
                    "Course fit emerges from the interaction between a golfer's skill shape and a course's characteristics. Each course is profiled by how much it rewards or punishes different skill areas — distance off the tee, accuracy, approach precision, short game, and putting. A links course exposed to wind rewards different attributes than a tree-lined parkland. The fit calculation adjusts expected scoring: a well-suited golfer gains a fractional advantage, while a poor fit costs strokes.",
                ]}
            />

            <H2Heading>Situational factors</H2Heading>
            <ArticleParagraph
                lines={[
                    "Two additional dimensions influence tournament outcomes. Weather resilience describes how a golfer copes with wind and rain — some players are relatively unaffected, others add strokes in difficult conditions. Pressure handling captures the tendency to perform better or worse under competitive stress, particularly in the closing stages of a tournament. These factors are subtle — fractions of a stroke — but in a close tournament they can be decisive.",
                ]}
            />

            <H2Heading>The radar chart</H2Heading>
            <ArticleParagraph
                lines={[
                    "The golfer profile is visualised as a pentagon radar chart, with each vertex representing one of the five key dimensions: off the tee, approach, around the green, putting, and consistency. A golfer with strengths across all areas and high consistency fills the pentagon to its edges; an inconsistent player with uneven skills produces a smaller, irregular shape. The chart provides a quick visual fingerprint of how a golfer's game is constructed, making it easy to compare profiles and anticipate course fit at a glance.",
                ]}
            />

            <div className="mt-6 flex flex-wrap gap-4">
                <Link
                    to="/sports-models/golf/profiles"
                    className="text-sm text-green-700 hover:text-green-900 underline"
                >
                    See the model in action in the tournament simulation
                </Link>
                <Link
                    to="/sports-models/golf/methodology"
                    className="text-sm text-green-700 hover:text-green-900 underline"
                >
                    Read the full methodology
                </Link>
            </div>
        </PageBody>
    );
};
